import { mix } from 'polished';
import styled, { css, keyframes } from 'styled-components';

import { convertHexToRgba, getFontStyle, rem, toRemString } from '../../utils';

const lightAlertColor = css`
  ${({ theme }) => mix(0.7, theme.Color.Feedback.Alert, '#FFFFFF')}
`;

export type CustomButtonProps = {
  textColor: string;
  primaryColor: string;
  secondaryColor: string;
  primaryActiveColor?: string;
};

export type ButtonVariant = 'custom' | 'primary' | 'secondary' | 'ghost' | 'shadow' | 'outline';

export type ButtonSize = 'Small' | 'Medium';

export type ButtonIconPosition = 'start' | 'end';

export const StyledIcon = styled.span`
  display: inline-flex;
`;

export type ButtonShape = 'default' | 'circular';

const primaryVariant = css<{ $active: boolean; $custom?: CustomButtonProps; $danger?: boolean }>(
  ({ theme, $active, $custom, $danger }) => css`
    background: ${$custom?.primaryColor ??
    ($danger ? theme.Color.Feedback.Alert : theme.Color.Brand.Primary)};
    color: ${$custom?.textColor ??
    theme.Color.TextContrast.Primary ??
    theme.Color.NeutralsFixed.Paper};

    ${StyledIcon} svg {
      fill: ${$custom?.textColor ??
      theme.Color.TextContrast.Primary ??
      theme.Color.NeutralsFixed.Paper};
    }

    &:hover,
    &:active {
      background: ${$custom?.secondaryColor ??
      ($danger ? lightAlertColor : theme.Color.Brand.Dark)};
    }

    ${$active &&
    css`
      background: ${$custom?.secondaryColor ??
      ($danger ? lightAlertColor : theme.Color.Brand.Dark)};
    `}

    &[disabled],
    &.disabled {
      background: ${theme.Color.Neutrals.Light};
      color: ${theme.Color.Neutrals.Gray};

      ${StyledIcon} svg {
        fill: ${theme.Color.TextContrast.Secondary ?? theme.Color.NeutralsFixed.Gray};
      }
    }
  `
);

const custom = css<{
  $active: boolean;
  $color?: string;
  $opacity?: number;
  $textColor?: string;
  $textColorHover?: string;
}>`
  background: ${({ $color }) => $color};
  color: ${({ theme, $textColor }) => $textColor ?? theme.Color.NeutralsFixed.Text};

  ${StyledIcon} svg {
    fill: ${({ theme }) => theme.Color.NeutralsFixed.Dark};
  }

  &:hover {
    color: ${({ theme, $textColorHover, $textColor }) =>
      $textColorHover ?? $textColor ?? theme.Color.NeutralsFixed.Text};
    background: ${({ $color, theme, $opacity }) =>
      convertHexToRgba($color ?? theme.Color.Brand.Primary, $opacity ?? 50)};
  }

  ${({ $active, $color, theme, $opacity }) =>
    $active &&
    css`
      background: ${convertHexToRgba($color ?? theme.Color.Brand.Primary, $opacity ?? 50)};
    `}
`;

const secondaryVariant = css<{
  $active: boolean;
  $custom?: CustomButtonProps;
  $danger?: boolean;
}>`
  background: ${({ theme, $custom, $danger }) =>
    $custom?.secondaryColor ??
    ($danger ? theme.Color.Feedback.AlertLight : theme.Color.Neutrals.Light)};
  color: ${({ theme, $custom, $danger }) =>
    $custom?.textColor ?? ($danger ? theme.Color.Feedback.Alert : theme.Color.Neutrals.Dark)};

  ${StyledIcon} {
    fill: ${({ theme, $danger }) =>
      $danger ? theme.Color.Feedback.Alert : theme.Color.Neutrals.Dark};
  }

  &:hover,
  &:active {
    color: ${({ theme, $custom, $danger }) =>
      $custom?.primaryColor ?? ($danger ? lightAlertColor : theme.Color.Brand.Primary)};

    ${StyledIcon} {
      fill: ${({ theme, $custom, $danger }) =>
        $custom?.primaryColor ?? ($danger ? lightAlertColor : theme.Color.Brand.Primary)};
    }
  }

  ${({ $active, $custom, $danger }) =>
    $active &&
    css`
      color: ${({ theme }) =>
        $custom?.primaryColor ?? ($danger ? lightAlertColor : theme.Color.Brand.Primary)};

      ${StyledIcon} {
        fill: ${({ theme }) =>
          $custom?.primaryColor ?? ($danger ? lightAlertColor : theme.Color.Brand.Primary)};
      }
    `}

  &[disabled],
  &.disabled {
    background: ${({ theme }) => theme.Color.Neutrals.Light};
    color: ${({ theme }) => theme.Color.Neutrals.Gray};
  }
`;

const ghostVariant = css<{ $active: boolean; $custom?: CustomButtonProps; $danger?: boolean }>`
  background: transparent;
  color: ${({ theme, $custom, $danger }) =>
    $custom?.textColor ?? ($danger ? theme.Color.Feedback.Alert : theme.Color.Neutrals.Dark)};
  &:hover,
  &:active {
    background: ${({ theme }) => theme.Components.Panel.BackgroundHover};
  }

  ${StyledIcon} {
    fill: ${({ theme, $danger }) =>
      $danger ? theme.Color.Feedback.Alert : theme.Color.Neutrals.Dark};
  }

  ${({ $active }) =>
    $active &&
    css`
      background: ${({ theme }) => theme.Components.Panel.BackgroundHover};
    `}

  &[disabled],
  &.disabled {
    background: transparent;
    color: ${({ theme }) => theme.Color.Neutrals.Gray};

    ${StyledIcon} {
      fill: ${({ theme }) => theme.Color.Neutrals.Text};
    }
  }
`;

const shadowVariant = css<{ $active: boolean; $custom?: CustomButtonProps; $danger?: boolean }>`
  background: ${({ theme }) => theme.Color.Neutrals.Paper};
  color: ${({ theme, $custom, $danger }) =>
    $custom?.textColor ?? ($danger ? theme.Color.Feedback.Alert : theme.Color.Neutrals.Dark)};
  box-shadow: ${({ theme }) => `0 0 ${theme.Spacing.Size.XSmall}px ${theme.Color.Overlay.Light}`};

  ${StyledIcon} {
    fill: ${({ theme, $danger }) =>
      $danger ? theme.Color.Feedback.Alert : theme.Color.Neutrals.Dark};
  }

  &:hover,
  &:active {
    color: ${({ theme, $custom, $danger }) =>
      $custom?.primaryColor ?? ($danger ? lightAlertColor : theme.Color.Brand.Primary)};

    ${StyledIcon} {
      fill: ${({ theme, $custom, $danger }) =>
        $custom?.primaryColor ?? ($danger ? lightAlertColor : theme.Color.Brand.Primary)};
    }
  }

  ${({ theme, $active, $custom, $danger }) =>
    $active &&
    css`
      color: ${$custom?.primaryColor ?? ($danger ? lightAlertColor : theme.Color.Brand.Primary)};

      ${StyledIcon} {
        fill: ${$custom?.primaryColor ?? ($danger ? lightAlertColor : theme.Color.Brand.Primary)};
      }
    `}

  &[disabled],
  &.disabled {
    color: ${({ theme }) => theme.Color.Neutrals.Gray};
  }
`;

const outlineVariant = css<{ $active: boolean; $custom?: CustomButtonProps; $danger?: boolean }>`
  background: transparent;
  color: ${({ theme, $custom, $danger }) =>
    $custom?.textColor ??
    ($danger ? theme.Color.Feedback.Alert : theme.Button.Variant.Outline.Color.Content)};

  box-shadow: ${({ theme, $custom, $danger }) =>
      `0 0 0 1px ${
        $custom?.primaryColor ??
        ($danger ? theme.Color.Feedback.Alert : theme.Button.Variant.Outline.Color.Border)
      }`}
    inset;

  ${StyledIcon} {
    fill: ${({ theme, $custom, $danger }) =>
      $custom?.textColor ??
      ($danger ? theme.Color.Feedback.Alert : theme.Button.Variant.Outline.Color.Content)};
  }

  &:hover,
  &:active {
    color: ${({ theme, $custom, $danger }) =>
      $custom?.primaryActiveColor ?? ($danger ? lightAlertColor : theme.Color.Brand.Primary)};

    box-shadow: ${({ theme, $custom, $danger }) =>
        `0 0 0 1px ${
          $custom?.primaryActiveColor ?? ($danger ? lightAlertColor : theme.Color.Brand.Primary)
        }`}
      inset;

    ${StyledIcon} {
      fill: ${({ theme, $custom, $danger }) =>
        $custom?.primaryActiveColor ?? $custom?.primaryColor ?? $danger
          ? lightAlertColor
          : theme.Color.Brand.Primary};
    }
  }

  ${({ theme, $active, $custom, $danger }) =>
    $active &&
    css`
      color: ${({ theme }) =>
        $custom?.primaryActiveColor ?? ($danger ? lightAlertColor : theme.Color.Brand.Primary)};

      box-shadow: ${({ theme }) =>
          `0 0 0 1px ${
            $custom?.primaryActiveColor ?? ($danger ? lightAlertColor : theme.Color.Brand.Primary)
          }`}
        inset;

      ${StyledIcon} {
        fill: ${$custom?.primaryActiveColor ??
        $custom?.primaryColor ??
        ($danger ? theme.Color.Feedback.Alert : theme.Color.Brand.Primary)};
      }
    `}

  &[disabled],
  &.disabled {
    background: ${({ theme }) => theme.Color.Neutrals.Paper};
    color: ${({ theme }) => theme.Color.Neutrals.Text};
    box-shadow: ${({ theme }) => `0 0 0 1px ${theme.Color.Neutrals.Text}`} inset;
    ${StyledIcon} {
      fill: ${({ theme }) => theme.Color.Neutrals.Text};
    }
  }
`;

export const StyledButton = styled.button<{
  $active: boolean;
  $opacity?: number;
  $color?: string;
  $size: ButtonSize;
  $variant: ButtonVariant;
  disabled: boolean;
  $iconPosition: ButtonIconPosition;
  $shape: ButtonShape;
  $textColor?: string;
  $textColorHover?: string;
  $custom?: CustomButtonProps;
  $danger?: boolean;
}>`
  white-space: nowrap;
  box-sizing: border-box;
  border: ${({ theme }) => rem(theme.Border.Size.Small)} solid transparent;
  cursor: pointer;
  display: inline-flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  flex-direction: ${({ $iconPosition }) => ($iconPosition === 'start' ? 'row' : 'row-reverse')};
  gap: ${({ theme }) => rem(theme.Spacing.Size.XXSmall)};
  outline: none;

  ${getFontStyle('Body')}

  user-select: none;
  transition: all ${({ theme }) => theme.Transition.Timing.Out};

  &:hover {
    transition: all ${({ theme }) => theme.Transition.Timing.In};
  }

  &:active {
    transition: all ${({ theme }) => theme.Transition.Timing.In};
  }

  &[disabled],
  &.disabled {
    cursor: default;
  }

  ${({ $variant }) => {
    switch ($variant) {
      case 'secondary':
        return secondaryVariant;
      case 'ghost':
        return ghostVariant;
      case 'shadow':
        return shadowVariant;
      case 'outline':
        return outlineVariant;
      case 'custom':
        return custom;
      case 'primary':
      default:
        return primaryVariant;
    }
  }}

  ${({ $shape }) => css`
    border-radius: ${$shape === 'circular' ? '50%' : 'inherit'};
  `}

  ${({ $size, $shape, theme }) => {
    switch ($size) {
      case 'Small':
        return css`
          min-height: ${rem(theme.Button.Size.Small)};
          border-radius: ${$shape === 'circular' ? '50%' : rem(theme.Border.Radius.Small)};
          padding: ${({ theme }) => toRemString(theme.Button.Spacing.Small)};
        `;
      case 'Medium':
      default:
        return css`
          min-height: ${rem(theme.Button.Size.Medium)};
          border-radius: ${$shape === 'circular' ? '50%' : rem(theme.Border.Radius.Medium)};
          padding: ${({ theme }) => toRemString(theme.Button.Spacing.Medium)};
        `;
    }
  }}
`;

const LoaderAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

export const StyledButtonLoader = styled.div`
  ${({ theme }) => css`
    width: ${rem(theme.Icon.Size.Medium)};
    height: ${rem(theme.Icon.Size.Medium)};
    border: ${rem(theme.Border.Size.Large)} solid ${theme.Color.Neutrals.Paper};
    border-bottom-color: ${theme.Color.Brand.Primary};
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: ${LoaderAnimation} 1s linear infinite;
  `}
`;
